import { useState, useEffect, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Cookies from 'universal-cookie';
import PropTypes from 'prop-types';

import { request } from '@apis/utils';
import { isOfTypeUuId } from '@hiredigital/lib/helpers/utils';
import { skipStaticBuild } from '@helpers/utils';

import BaseLayout from '@components/Layout/BaseLayout';

import ResumeHeader from '@components/Profile/ResumeHeader';
import Profile from '@components/Profile/ResumeProfile';
import StickyProfile from '@components/Profile/StickyProfile';
import Availability from '@components/Profile/Availability';
import Highlights from '@components/Profile/Highlights';
import Portfolios from '@components/Profile/Portfolios';

import { PortfolioType } from '@hiredigital/lib/helpers/marketingEnum';

import Styles from './Styles.module.scss';
const TalentResume = dynamic(() => import('@components/Profile/Resume'), {
  ssr: false,
  suspense: true,
});
const Education = dynamic(() => import('@components/Profile/Education'), {
  ssr: false,
  suspense: true,
});

const Footer = dynamic(() => import('@components/Footer/Footer'), { ssr: false, suspense: true });

const cookies = new Cookies();

const Resume = ({ user, portfolio, username, highlightEducation, highlightEmployment }) => {
  const router = useRouter();
  const { slug } = router.query;

  // Set initial state vars
  const [hasToken, setHasToken] = useState(false);
  const [employmentCount, setEmploymentCount] = useState(4);
  const [educationCount, setEducationCount] = useState(4);
  const [employmentToggled, setEmploymentToggled] = useState(false);
  const [educationToggled, setEducationToggled] = useState(false);
  // const [isValidPortfolio, setValidPortfolio] = useState(isValidPortfolioId);

  const head = {
    title: `${user.name} | Hire Digital`,
    meta: [
      {
        name: 'description',
        content: user.profile?.description || 'Work With High Performing Talent On Demand',
      },
      { name: 'image', content: user.profile && user.profile.picture },
      { name: 'og:type', content: 'profile' },
      { name: 'profile:username', content: username },
      { name: 'profile:first_name', content: user?.firstName },
      { name: 'profile:last_name', content: user?.lastName },
      { name: 'robots', content: 'noindex' },
    ],
  };

  const toggleEmployment = () => {
    if (employmentCount === 4) {
      setEmploymentCount(undefined);
    } else {
      setEmploymentCount(4);
      setEmploymentToggled(!employmentToggled);
    }
  };

  const toggleEducation = () => {
    if (educationCount === 4) {
      setEducationCount(undefined);
    } else {
      setEducationCount(4);
      setEducationToggled(!educationToggled);
    }
  };

  const handlePortfolioNavigate = (item) => {
    if (item) {
      router.replace(`/resumes/${username}/items/${item?.uuid}`, undefined, { shallow: true });
    } else {
      router.replace(`/resumes/${username}`, undefined, { shallow: true });
    }
  };

  useEffect(() => {
    // On mount
    if (cookies.get('token')) {
      setHasToken(true);
    }
  }, []);

  const portfolioItemUuid = slug?.[2];

  return (
    <BaseLayout pageHead={head}>
      <div className={Styles.main} id='scrollContainer'>
        <div className={Styles.wrapper}>
          <ResumeHeader user={user} hasToken={hasToken} />
          <div className={Styles.container}>
            <Profile user={user} />
            <StickyProfile user={user} hasToken={hasToken} />
            <Availability user={user} hasToken={hasToken} />
            <Highlights
              {...{
                highlightEmployment,
                highlightEducation,
                employmentCount,
                educationCount,
                toggleEmployment,
                toggleEducation,
              }}
            />
            <Portfolios
              {...{
                user,
                portfolio,
                resourceUuid: isOfTypeUuId(username) ? user?.uuid : username,
                type: isOfTypeUuId(username) ? PortfolioType.PUBLIC_RESUME : PortfolioType.RESUME,
                handlePortfolioNavigate,
                portfolioItemUuid,
              }}
            />
            <Suspense>
              <TalentResume user={user} />
              <Education user={user} />
            </Suspense>
          </div>
        </div>
        <Suspense>
          <Footer className={Styles.footer} />
        </Suspense>
      </div>
    </BaseLayout>
  );
};

Resume.propTypes = {
  username: PropTypes.string,
  user: PropTypes.shape({
    profile: PropTypes.object,
    firstName: PropTypes.string,
    username: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    employment: PropTypes.array,
    education: PropTypes.array,
  }),
  portfolio: PropTypes.object,
};

export default Resume;

export const getStaticProps = async ({ params }) => {
  const {
    slug: [username, items, itemUuid],
  } = params;
  const isUuid = isOfTypeUuId(username);
  try {
    const [{ data: user }, { data: portfolio }] = await Promise.all([
      request.get(
        isUuid
          ? `${process.env.NEXT_PUBLIC_API_URL}/public/resumes/${username}/`
          : `${process.env.NEXT_PUBLIC_API_URL}/resumes/${username}/`
      ),
      request.get(
        isUuid
          ? `${process.env.NEXT_PUBLIC_API_URL}/public/resumes/${username}/items/`
          : `${process.env.NEXT_PUBLIC_API_URL}/resumes/${username}/items/`
      ),
    ]);

    // eslint-disable-next-line no-unneeded-ternary
    const isValidPortfolioPath = username && items && itemUuid ? true : false; // /[username]/items/[itemId]
    // eslint-disable-next-line no-unneeded-ternary
    const isValidResumePath = username && !items && !itemUuid ? true : false; // /[username]

    if (!isValidPortfolioPath && !isValidResumePath) {
      console.error('Not a valid path');
      return {
        notFound: true,
        revalidate: 720,
      };
    }

    const highlightEmployment = user.employment.filter((item) => item.highlight);
    const highlightEducation = user.education.filter((item) => item.highlight);

    return {
      props: { params, user, portfolio, username, highlightEducation, highlightEmployment },
      revalidate: 720,
    };
  } catch (err) {
    console.error(err);
    return {
      notFound: true,
      revalidate: 720,
    };
  }
};

export const getStaticPaths = async () => {
  if (skipStaticBuild) return { paths: [], fallback: 'blocking' };

  // TODO add paths for static generation
  return {
    paths: [],
    fallback: 'blocking',
  };
};
